import moment from "moment-timezone";
import { z } from "zod";
import { CommonDestinationTimezone } from "@/lib/utils";

export const ApproveDocumentDispatchRequestValidation = z.object({
  queue_entry_id: z.string().uuid(),
}).strict();

export const HideDocumentDispatchRequestValidation = z.object({
  queue_entry_id: z.string().uuid(),
  reason: z.string().trim().min(1, "A reason is required"),
}).strict();

export const RetryDocumentDispatchRequestValidation = z.object({
  queue_entry_id: z.string().uuid(),
}).strict();

export const SendDocumentDispatchRequestValidation = z.object({
  queue_entry_id: z.string().uuid(),
  is_test_send: z.boolean(),
}).strict();

export const BulkSendDocumentDispatchRequestValidation = z.object({
  queue_entry_ids: z.array(z.string().uuid()).min(1),
  is_test_send: z.boolean(),
  approve: z.boolean(),
}).strict().superRefine((value, context: z.RefinementCtx): void => {
  if (new Set(value.queue_entry_ids).size !== value.queue_entry_ids.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: "Document dispatch queue entry IDs must be unique", path: ["queue_entry_ids"] });
  }
});

const documentDispatchReportPortalDateValidation = z.string().refine(
  (value: string): boolean => moment
    .tz(value, "YYYY-MM-DD", true, CommonDestinationTimezone)
    .isValid(),
  { message: "Date must use YYYY-MM-DD format" },
);

const DocumentDispatchEversignStatusReportRequestBaseValidation = z.object({
  feature_type: z.enum(["otc_portal_amendment", "agtech_email_contract", "deferred_payment_contract"]),
  preset: z.enum(["automated_queued", "manual_queued"]).optional(),
  preparation_status: z.enum(["queued", "processing", "ready", "failed"]).optional(),
  queue_status: z.enum(["not_queued", "queued", "processing", "submitted", "failed"]).optional(),
  delivery_mode: z.enum(["automatic", "manual"]).optional(),
  review_status: z.enum(["pending", "approved", "not_required"]).optional(),
  visibility_status: z.literal("visible"),
  date_field: z.enum(["sent_at", "signed_at"]),
  after_date: documentDispatchReportPortalDateValidation.optional(),
  before_date: documentDispatchReportPortalDateValidation.optional(),
}).strict();

type DocumentDispatchEversignStatusReportRequestBase = z.infer<
  typeof DocumentDispatchEversignStatusReportRequestBaseValidation
>;

export const DocumentDispatchEversignStatusReportRequestValidation =
  DocumentDispatchEversignStatusReportRequestBaseValidation.superRefine(
    (
      value: DocumentDispatchEversignStatusReportRequestBase,
      context: z.RefinementCtx,
    ): void => {
      if (
        value.after_date
        && value.before_date
        && moment.tz(value.after_date, "YYYY-MM-DD", true, CommonDestinationTimezone)
          .isAfter(moment.tz(value.before_date, "YYYY-MM-DD", true, CommonDestinationTimezone))
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "From date must be on or before to date",
          path: ["before_date"],
        });
      }
    },
  );

export type ApproveDocumentDispatchRequest = z.infer<typeof ApproveDocumentDispatchRequestValidation>;
export type HideDocumentDispatchRequest = z.infer<typeof HideDocumentDispatchRequestValidation>;
export type RetryDocumentDispatchRequest = z.infer<typeof RetryDocumentDispatchRequestValidation>;
export type SendDocumentDispatchRequest = z.infer<typeof SendDocumentDispatchRequestValidation>;
export type BulkSendDocumentDispatchRequest = z.infer<typeof BulkSendDocumentDispatchRequestValidation>;
export type DocumentDispatchEversignStatusReportRequest = z.infer<typeof DocumentDispatchEversignStatusReportRequestValidation>;
